import Cookies from 'js-cookie';
import { newExpiresAtDate } from './utils';
import { v4 as uuidv4 } from 'uuid';

export const ANALYTICS_CONTEXT_COOKIE_NAME = 'ANALYTICS_CONTEXT';
export const CONSENT_STATUS_COOKIE = 'COOKIES_ALLOWED';
export const ANON_USER_ID_COOKIE = 'ANON_USER_ID';
export const CONSENT_COOKIE_EXPIRE_AFTER_MONTH = 6;

interface ConsentCookieValue {
  isAllowed: boolean;
  expiresAt: number;
}

export enum ConsentCookieState {
  Empty,
  Expired,
  Allowed,
  Declined
}

export function currentConsentCookieState(): ConsentCookieState {
  const consentCookie = Cookies.get(CONSENT_STATUS_COOKIE);

  if (!consentCookie) {
    return ConsentCookieState.Empty;
  }

  const consentCookieData: ConsentCookieValue = JSON.parse(consentCookie);
  const currentTimestamp = new Date().getTime();

  if (currentTimestamp > consentCookieData.expiresAt) {
    return ConsentCookieState.Expired;
  }

  return consentCookieData.isAllowed ? ConsentCookieState.Allowed : ConsentCookieState.Declined;
}

export function setConsentCookie(isAllowed: boolean) {
  const expiresAtDate = newExpiresAtDate(CONSENT_COOKIE_EXPIRE_AFTER_MONTH);
  const cookieValue: ConsentCookieValue = {
    isAllowed,
    expiresAt: expiresAtDate.getTime()
  };

  Cookies.set(CONSENT_STATUS_COOKIE, JSON.stringify(cookieValue), {
    expires: expiresAtDate
  });
}

export function setAnonUserIdIfConsented(): void {
  const consentState = currentConsentCookieState();

  if (consentState === ConsentCookieState.Allowed || consentState === ConsentCookieState.Empty) {
    const existingAnonId = Cookies.get(ANON_USER_ID_COOKIE);
    if (!existingAnonId) {
      const anonId = uuidv4();
      const expiresAtDate = newExpiresAtDate(CONSENT_COOKIE_EXPIRE_AFTER_MONTH);

      Cookies.set(ANON_USER_ID_COOKIE, anonId, {
        expires: expiresAtDate
      });
    }
  } else {
    // User hasn't consented or has declined cookies, clear the cookie
    Cookies.remove(ANON_USER_ID_COOKIE);
  }
}

export function parseCookiesFromRequest(request: Request): Record<string, string> {
  const cookieHeader = request.headers.get('cookie');
  return cookieHeader
    ? Object.fromEntries(cookieHeader.split(';').map((cookie) => cookie.trim().split('=')))
    : {};
}

export function getAnonUserIdFromRequest(request: Request): string | undefined {
  const cookies = parseCookiesFromRequest(request);
  return cookies[ANON_USER_ID_COOKIE];
}
